const VOICE_WAIT_MS = 1500;
export const SPEAK_GAP_MS = 220;
export const GHOST_END_MS = 140;
export const KEEP_ALIVE_MS = 12000;
export const RATE_OPTIONS = [0.8, 1, 1.2, 1.5];

const PREFERRED_VOICE =
  /natural|premium|enhanced|neural|samantha|daniel|ting-ting|tingting|mei-jia|meijia|sin-ji|sinji|li-mu|limu|yu-shu|yushu|xiaoxiao|yunyang|huihui|yaoyao|kangkang|google|microsoft|siri|hanhan/;
const AVOID_VOICE =
  /compact|eloquence|novelty|bells|zarvox|whisper|boing|bad news|pipe|trinoids|cellos|organ|albert|bubbles|junior|kathy|princess/;

export const isSpeechSupported = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  "SpeechSynthesisUtterance" in window;

export const isLikelyIOS = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export const langPrefix = (lang = "") =>
  String(lang).toLowerCase().startsWith("zh") ? "zh" : "en";

export const speakableText = (text) =>
  String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, " ")
    .trim();

export const loadVoices = () => {
  if (!isSpeechSupported()) return Promise.resolve([]);
  const synth = window.speechSynthesis;
  const existing = synth.getVoices();
  if (existing.length) return Promise.resolve(existing);

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      synth.removeEventListener("voiceschanged", onChange);
      resolve(synth.getVoices());
    };
    const onChange = () => {
      if (synth.getVoices().length) finish();
    };
    synth.addEventListener("voiceschanged", onChange);
    window.setTimeout(finish, VOICE_WAIT_MS);
  });
};

export const voicesForLang = (voices, lang) => {
  if (!voices?.length || !lang) return [];
  const prefix = langPrefix(lang);
  return voices.filter((voice) =>
    String(voice.lang || "")
      .toLowerCase()
      .startsWith(prefix)
  );
};

const rankVoice = (voice, wanted) => {
  const name = String(voice.name || "").toLowerCase();
  const voiceLang = String(voice.lang || "").toLowerCase();
  let score = 0;
  if (voice.localService) score += 2;
  if (voiceLang.startsWith(wanted)) score += 3;
  if (PREFERRED_VOICE.test(name)) score += 4;
  if (AVOID_VOICE.test(name)) score -= 8;
  return score;
};

export const pickVoice = (voices, lang, preferredURI) => {
  const matches = voicesForLang(voices, lang);
  if (!matches.length) return null;
  if (preferredURI) {
    const chosen = matches.find((voice) => voice.voiceURI === preferredURI);
    if (chosen) return chosen;
  }
  const wanted = String(lang || "").toLowerCase();
  return [...matches].sort((a, b) => rankVoice(b, wanted) - rankVoice(a, wanted))[0] || null;
};

export const createUtterance = (text, lang, voices, options = {}) => {
  const utterance = new window.SpeechSynthesisUtterance(speakableText(text));
  const attachVoice = options.attachVoice !== false;
  const voice = attachVoice ? pickVoice(voices, lang, options.voiceURI) : null;
  utterance.lang = voice?.lang || lang;
  if (voice) utterance.voice = voice;
  const base = langPrefix(lang) === "zh" ? 0.92 : 0.96;
  const rate = Number(options.rate);
  utterance.rate = Math.min(2, Math.max(0.5, base * (Number.isFinite(rate) ? rate : 1)));
  utterance.pitch = 1;
  utterance.volume = 1;
  return utterance;
};

export const isGhostSpeechEnd = ({ started, elapsedMs }) =>
  !started || (Number(elapsedMs) >= 0 && elapsedMs < GHOST_END_MS);

export const speakNow = (utterance, { resume = false } = {}) => {
  if (!isSpeechSupported()) return;
  const synth = window.speechSynthesis;
  if (resume) {
    try {
      synth.resume();
    } catch (error) {
      // Some engines throw if nothing is paused.
    }
  }
  try {
    synth.speak(utterance);
  } catch (error) {
    window.setTimeout(() => {
      utterance.onerror?.({ error: "synthesis-failed" });
    }, 0);
  }
};

export const cancelSpeech = () => {
  if (!isSpeechSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch (error) {
    // ignore engine quirks
  }
};

export const pauseSpeech = () => {
  if (!isSpeechSupported()) return false;
  const synth = window.speechSynthesis;
  try {
    synth.pause();
    return synth.paused || !synth.speaking;
  } catch (error) {
    return false;
  }
};

export const resumeSpeech = () => {
  if (!isSpeechSupported()) return false;
  const synth = window.speechSynthesis;
  try {
    synth.resume();
    return synth.speaking || synth.paused === false;
  } catch (error) {
    return false;
  }
};

export const scrollBehavior = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "smooth";
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
};

export const isIgnorableSpeechError = (error) =>
  error === "canceled" || error === "interrupted" || error === "not-allowed";
